import { useEffect, useRef } from 'react';

/**
 * ESC peels one layer at a time: dismiss the inner layer (a history panel) if
 * it is open, otherwise close the surface itself.
 *
 * Shared by the global AI assistant drawer and the legacy CustomDash AI
 * assistant drawer — both stack a history panel inside a drawer and each
 * carried its own copy of this listener. Only the peel SEMANTICS are shared:
 * the two drawers' layouts, styling and focus models are deliberately
 * separate.
 *
 * Callbacks are read through a ref so the window listener binds once per open
 * instead of re-subscribing on every state change, which both copies did.
 */
export function useEscapePeel({
  enabled,
  isInnerOpen,
  closeInner,
  closeOuter,
}: {
  /** Only listen while the surface itself is open. */
  enabled: boolean;
  /** Whether the inner layer is showing — it takes the first ESC. */
  isInnerOpen: boolean;
  closeInner: () => void;
  closeOuter: () => void;
}): void {
  const latest = useRef({ isInnerOpen, closeInner, closeOuter });
  useEffect(() => {
    latest.current = { isInnerOpen, closeInner, closeOuter };
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }
      const current = latest.current;
      if (current.isInnerOpen) {
        current.closeInner();
        return;
      }
      current.closeOuter();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);
}
